import { computed, Ref } from 'vue'
import { FragmentType, useFragment } from '@/gql'
import { FieldableFormCustomFieldFragment } from '@/components/Fieldable/graphql/fragments'
import {
  CustomFieldTypeValueInput,
  FieldableFormCustomFieldFragment as FieldableFormCustomFieldFragmentGQL,
} from '@/gql/graphql'
import { isCustomFieldRequired } from '@/components/Fields/utils/CustomFieldUtils'

interface MappedField {
  field: FragmentType<typeof FieldableFormCustomFieldFragment>
  fieldTyped: FieldableFormCustomFieldFragmentGQL
  fieldValue?: CustomFieldTypeValueInput
}

export default function useFormCustomFieldValues(
  fields: Ref<FragmentType<typeof FieldableFormCustomFieldFragment>[]>,
  fieldValues: Ref<CustomFieldTypeValueInput[]>,
) {
  const mappedFields = computed<MappedField[]>(() =>
    fields.value.map((field) => ({
      field,
      fieldTyped: useFragment(FieldableFormCustomFieldFragment, field),
      fieldValue: fieldValues.value?.find((fieldValue) =>
        [fieldValue.singleSelect?.id, fieldValue.freeText?.id].includes(
          useFragment(FieldableFormCustomFieldFragment, field).id,
        ),
      ),
    })),
  )

  const mappedAndSortedFields = computed(() =>
    mappedFields.value.sort((valueA, valueB) => {
      const aIsRequired = isCustomFieldRequired(valueA.fieldTyped.settings)
      const bIsRequired = isCustomFieldRequired(valueB.fieldTyped.settings)
      if (aIsRequired && !bIsRequired) {
        return -1
      }
      if (!aIsRequired && bIsRequired) {
        return 1
      }
      return 0
    }),
  )

  const parseUpdateFieldValues = (
    fieldId: string,
    value: CustomFieldTypeValueInput,
  ): CustomFieldTypeValueInput[] => {
    const index = mappedFields.value.findIndex(
      (localMappedField) => localMappedField.fieldTyped.id === fieldId,
    )

    const updatedFieldValues = mappedFields.value
    updatedFieldValues[index].fieldValue = value

    return updatedFieldValues
      .map((mappedField) => mappedField.fieldValue)
      .filter((fieldValue) => fieldValue) as CustomFieldTypeValueInput[]
  }

  return {
    mappedFields,
    mappedAndSortedFields,
    parseUpdateFieldValues,
  }
}
